import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Coordinates } from '../models';

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  private scriptLoadingPromise: Promise<void> | null = null;
  private geocodeCache = new Map<string, Coordinates>(); // Cache to reduce redundant API calls
  private lastFetchedLocation: Coordinates | null = null; // Store last geocoded location

  constructor(private http: HttpClient) {}

  /** Load Google Maps Script */
  load(apiKey: string, mapEnabled: boolean): Promise<void> {
    if (this.scriptLoadingPromise) return this.scriptLoadingPromise;

    if (!apiKey) {
      console.error('Google API Key not injected');
      return Promise.reject('Google API Key not injected');
    }

    this.scriptLoadingPromise = new Promise<void>((resolve, reject) => {
      const scriptId = 'google-maps-script';
      if (document.getElementById(scriptId)) {
        resolve(); // Already loaded
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;

      const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${mapEnabled ? 'places,drawing' : 'places'}`;

      if (window['trustedTypes'] && window['defaultTrustedTypesPolicy']) {
        script.src = window['defaultTrustedTypesPolicy'].createScriptURL(
          scriptUrl
        ) as unknown as string;
      } else {
        script.src = scriptUrl;
      }

      script.onload = () => resolve();
      script.onerror = reject;

      document.head.appendChild(script);
    });

    return this.scriptLoadingPromise;
  }

  /** Get User's Current Location (with optional geocoding) */
  getCurrentLocation(apiKey: string, geoCoded = false): Observable<Coordinates> {
    return new Observable(observer => {
      if (this.lastFetchedLocation) {
        observer.next(this.lastFetchedLocation);
        observer.complete();
        return;
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const coords: Coordinates = {
              location: '',
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              coords: `${position.coords.latitude}, ${position.coords.longitude}`,
            };

            if (geoCoded) {
              this.getGeoCodedAddress(apiKey, coords).subscribe(geoCodedCoords => {
                this.lastFetchedLocation = geoCodedCoords; // Cache location
                observer.next(geoCodedCoords);
                observer.complete();
              });
            } else {
              observer.next(coords);
              observer.complete();
            }
          },
          () => {
            this.fallbackCurrentPosition(apiKey, geoCoded).subscribe(observer);
          }
        );
      } else {
        this.fallbackCurrentPosition(apiKey, geoCoded).subscribe(observer);
      }
    });
  }

  /** Get Address from Coordinates */
  getGeoCodedAddress(apiKey: string, coords: Coordinates): Observable<Coordinates> {
    const cacheKey = `${coords.latitude},${coords.longitude}`;

    if (this.geocodeCache.has(cacheKey)) {
      return of(this.geocodeCache.get(cacheKey)!); // Use cached result
    }

    const geocodeURL = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coords.latitude},${coords.longitude}&key=${apiKey}`;
    return this.http.get<any>(geocodeURL).pipe(
      map(response => {
        if (response.status === 'OK' && response.results[0]) {
          const formattedCoords = { ...coords, location: response.results[0].formatted_address };
          this.geocodeCache.set(cacheKey, formattedCoords); // Cache result
          return formattedCoords;
        }
        return coords;
      }),
      catchError(() => of(coords)) // Fallback to raw coordinates if geocoding fails
    );
  }

  /** Fallback: Get Location via IP */
  private fallbackCurrentPosition(apiKey: string, geoCoded: boolean): Observable<Coordinates> {
    const geolocateURL = `https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`;

    return this.http.post<any>(geolocateURL, {}).pipe(
      switchMap(response => {
        const coords: Coordinates = {
          location: '',
          latitude: response.location.lat,
          longitude: response.location.lng,
          coords: `${response.location.lat}, ${response.location.lng}`,
        };

        return geoCoded ? this.getGeoCodedAddress(apiKey, coords) : of(coords);
      }),
      catchError(() => of({ location: 'Unknown', latitude: 0, longitude: 0, coords: '0, 0' })) // Fallback if IP lookup fails
    );
  }
}
